import { TFunction } from "i18next";
import * as Yup from "yup";

import { IbanSchema } from "./FormikIbanField.types";

const IBAN_MIN_LENGTH = 15;
const IBAN_MAX_LENGTH = 34;

const IBAN_COUNTRY_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BI: 27,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DJ: 27,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HN: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MN: 20,
  MR: 27,
  MT: 31,
  MU: 30,
  NI: 28,
  NL: 18,
  NO: 15,
  OM: 23,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SO: 23,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
  YE: 30,
};

export function normalizeIban(value: string): string {
  return value.replace(/\s/g, "").toUpperCase();
}

export function formatIban(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return value.match(/.{1,4}/g)?.join(" ") ?? value;
}

/**
 * Whether a value is meant as an IBAN — a known country code, two check digits and at least the shortest IBAN
 * length — for fields that also accept other account or policy numbers.
 */
export function looksLikeIban(value: string): boolean {
  const canonical = normalizeIban(value);
  return (
    canonical.length >= IBAN_MIN_LENGTH &&
    IBAN_COUNTRY_LENGTHS[canonical.slice(0, 2)] !== undefined &&
    /^\d{2}$/.test(canonical.slice(2, 4))
  );
}

export function isValidIban(value: string): boolean {
  const canonical = normalizeIban(value);

  if (canonical.length < IBAN_MIN_LENGTH || canonical.length > IBAN_MAX_LENGTH) {
    return false;
  }

  if (!/^[A-Z0-9]+$/.test(canonical)) {
    return false;
  }

  const countryCode = canonical.slice(0, 2);
  const expectedLength = IBAN_COUNTRY_LENGTHS[countryCode];
  if (expectedLength === undefined || canonical.length !== expectedLength) {
    return false;
  }

  return mod97(canonical) === 1n;
}

function mod97(canonical: string): bigint {
  const rearranged = canonical.slice(4) + canonical.slice(0, 4);
  let numeric = "";
  for (const c of rearranged) {
    if (c >= "0" && c <= "9") {
      numeric += c;
    } else {
      numeric += String(c.charCodeAt(0) - "A".charCodeAt(0) + 10);
    }
  }
  return BigInt(numeric) % 97n;
}

// `common.iban.invalid` also exists in the internal app's own namespace: internal callers pass their
// app `t`, so the key resolves there; shared callers pass the shared-namespace `t`.
export const ibanValidation = (t: TFunction): IbanSchema => ({
  iban: Yup.string()
    .nullable()
    .optional()
    .test("iban", t("common.iban.invalid"), (value) => {
      if (!value) {
        return true;
      }
      return isValidIban(value);
    }),
});
